import { parseLocalDate, monthDayToFullDate } from './dateUtils';
import { normalizeCellPlants } from './constants';

/**
 * Planting Schedule Utility Functions
 * 
 * These utilities handle schedule calculations, harvest estimates, and status tracking
 * for the PlantingSchedule component.
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Fall growth factor - additional days added to harvest estimate for fall plantings.
 * Plants started after the season midpoint grow slower due to decreasing daylight.
 */
export const FALL_GROWTH_FACTOR_DAYS = 14;

/**
 * Default weeks indoors by season (used when seed data doesn't specify).
 */
export const DEFAULT_WEEKS_INDOORS = {
  warm: 8,
  cool: 4
};

/**
 * Planting status definitions with colors.
 */
export const PLANTING_STATUSES = {
  completed: {
    text: 'Completed',
    color: 'text-gray-500',
    bg: 'bg-gray-500',
    borderColor: '#6B7280',
    key: 'completed'
  },
  transplanted: {
    text: 'Transplanted',
    color: 'text-[#52A675]',
    bg: 'bg-[#52A675]',
    borderColor: '#52A675',
    key: 'transplanted'
  },
  germinated: {
    text: 'Germinated',
    color: 'text-[#F4A261]',
    bg: 'bg-[#F4A261]',
    borderColor: '#F4A261',
    key: 'germinated'
  },
  started: {
    text: 'Started',
    color: 'text-[#3CDEFA]',
    bg: 'bg-[#3CDEFA]',
    borderColor: '#3CDEFA',
    key: 'started'
  },
  planned: {
    text: 'Planned',
    color: 'text-[#C7F464]',
    bg: 'bg-[#C7F464]',
    borderColor: '#C7F464',
    key: 'planned'
  }
};

/**
 * Harvest season display labels.
 */
export const HARVEST_SEASONS = {
  spring: 'Spring',
  summer: 'Summer',
  fall: 'Fall',
};

/**
 * Normalize a harvest_season value to a string[] array.
 * Handles: string[] (already correct), legacy string IDs, and null/undefined.
 *   'spring'    → ['spring']
 *   'multi'     → ['spring', 'fall']
 *   'yearround' → ['spring', 'summer', 'fall']
 *   []          → []
 *   null/undef  → []
 */
export const normalizeHarvestSeason = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  switch (value) {
    case 'multi': return ['spring', 'fall'];
    case 'yearround': return ['spring', 'summer', 'fall'];
    default: return [value]; // 'spring', 'summer', 'fall' pass through
  }
};

// ============================================================================
// STATUS FUNCTIONS
// ============================================================================

/**
 * Get the current status of a planting based on its date fields.
 * @param {Object} planting - The planting object
 * @returns {Object} Status object with text, color, bg, borderColor, and key
 */
export const getPlantingStatus = (planting) => {
  if (planting.completed) return PLANTING_STATUSES.completed;
  if (planting.transplant_date) return PLANTING_STATUSES.transplanted;
  if (planting.germination_date) return PLANTING_STATUSES.germinated;
  if (planting.started_date) return PLANTING_STATUSES.started;
  return PLANTING_STATUSES.planned;
};

/**
 * Format harvest season for display.
 * Accepts a string ID ('spring'), an array (['spring','fall']), or null.
 * @param {string|string[]|null} season
 * @returns {string} Human-readable season name(s)
 */
export const formatHarvestSeason = (season) => {
  if (Array.isArray(season)) {
    if (season.length === 0) return '';
    return season.map(s => HARVEST_SEASONS[s] || s).join(' & ');
  }
  return HARVEST_SEASONS[season] || season || '';
};

// ============================================================================
// DATE CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate the midpoint between last frost and first frost dates.
 * Used to determine if a planting is a fall planting.
 * @param {Object} frostDates - Object with lastFrost and firstFrost date strings
 * @returns {Date|null} Midpoint date, or null if frost dates not available
 */
export const getSeasonMidpoint = (frostDates) => {
  if (!frostDates?.lastFrost || !frostDates?.firstFrost) return null;

  const lastFrost = parseLocalDate(frostDates.lastFrost);
  const firstFrost = parseLocalDate(frostDates.firstFrost);
  if (!lastFrost || !firstFrost) return null;

  const midpointTime = lastFrost.getTime() + (firstFrost.getTime() - lastFrost.getTime()) / 2;
  return new Date(midpointTime);
};

/**
 * Determine if a planting date is on or after the season midpoint (fall planting).
 * Fall plantings get an additional growth factor added to harvest estimates.
 * @param {Date} plantingDate - The planting date to check
 * @param {Object} frostDates - Object with lastFrost and firstFrost date strings
 * @returns {boolean} True if fall growth factor should be applied
 */
export const isFallPlanting = (plantingDate, frostDates) => {
  const midpoint = getSeasonMidpoint(frostDates);
  if (!midpoint || !plantingDate) return false;
  return plantingDate.getTime() >= midpoint.getTime();
};

/**
 * Format a date for short display (e.g., "May 15").
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Get effective weeks indoors based on seed data or season defaults.
 * @param {Object} seedData - Seed data object (may be null)
 * @returns {number|null} Weeks indoors, or null if cannot be determined
 */
export const getEffectiveWeeksIndoors = (seedData) => {
  if (seedData?.weeks_indoors_before_transplant) {
    return seedData.weeks_indoors_before_transplant;
  }
  if (seedData?.season === 'warm') return DEFAULT_WEEKS_INDOORS.warm;
  if (seedData?.season === 'cool') return DEFAULT_WEEKS_INDOORS.cool;
  return null;
};

// ============================================================================
// SEED DATA FUNCTIONS
// ============================================================================

/**
 * Find matching seed data for a plant/variety combination.
 * @param {Array} seeds - Array of seed objects from database
 * @param {string} plantName - Plant name to match
 * @param {string} varietyName - Variety name to match (optional)
 * @returns {Object|null} Matching seed object, or null if not found
 */
export const findSeedData = (seeds, plantName, varietyName) => {
  if (!plantName || !seeds?.length) return null;
  const normalizedPlant = plantName.toLowerCase().trim();
  const normalizedVariety = (varietyName || '').toLowerCase().trim();

  return seeds.find(s => {
    const seedPlant = (s.plant || '').toLowerCase().trim();
    const seedVariety = (s.variety || '').toLowerCase().trim();
    return seedPlant === normalizedPlant &&
      (normalizedVariety === '' || seedVariety === normalizedVariety);
  }) || null;
};

// ============================================================================
// SCHEDULE CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate planting/harvest schedule for a batch entry with succession planting.
 * Used in the add form to preview scheduled dates.
 * 
 * @param {Object} entry - Batch entry with plant, variety, start_method, successionEnabled, etc.
 * @param {string} startDateStr - Start date string (YYYY-MM-DD)
 * @param {Array} seeds - Array of seed objects for looking up days_to_production
 * @param {Object} frostDates - Object with lastFrost and firstFrost date strings
 * @returns {Object|null} Schedule result with schedule array and metadata, or null if invalid
 */
export const calculatePlantingSchedule = (entry, startDateStr, seeds, frostDates) => {
  if (!startDateStr || !entry.plant) return null;

  const seedData = findSeedData(seeds, entry.plant, entry.variety);
  const daysToProduction = seedData?.days_to_production;
  const weeksIndoors = seedData?.weeks_indoors_before_transplant;
  const season = seedData?.season;
  const isIndoor = entry.start_method === 'indoor';
  const hasFrostDates = !!(frostDates?.lastFrost && frostDates?.firstFrost);

  const count = entry.successionEnabled ? entry.successionCount : 1;
  const frequency = entry.successionFrequency || 7;

  const schedule = [];
  const baseDate = parseLocalDate(startDateStr);
  if (!baseDate) return null;

  // Determine weeks indoors: use seed data, or season-based default
  const effectiveWeeksIndoors = getEffectiveWeeksIndoors(seedData);

  for (let i = 0; i < count; i++) {
    const plantDate = new Date(baseDate);
    plantDate.setDate(plantDate.getDate() + (i * frequency));

    const item = {
      plantingNum: i + 1,
      plantDate: plantDate,
      transplantDate: null,
      harvestDate: null,
      canShowTransplant: false,
      canShowHarvest: false,
      hasFallFactor: false
    };

    if (isIndoor) {
      // Smart transplant date: if planting date MM-DD matches seed's planned start date,
      // use seed's planned transplant date applied to the planting's year
      const seedPlannedStartMMDD = seedData?.planned_start_date || '';
      const seedPlannedTransplantMMDD = seedData?.planned_transplant_date || '';
      const plantDateMMDD = `${String(plantDate.getMonth() + 1).padStart(2, '0')}-${String(plantDate.getDate()).padStart(2, '0')}`;

      if (seedPlannedStartMMDD && plantDateMMDD === seedPlannedStartMMDD && seedPlannedTransplantMMDD) {
        // Dates match - use seed's planned transplant date
        let transplantYear = plantDate.getFullYear();
        // If transplant MM-DD is before start MM-DD, it wraps to next year
        if (seedPlannedTransplantMMDD < seedPlannedStartMMDD) {
          transplantYear += 1;
        }
        const transplantFullDate = monthDayToFullDate(seedPlannedTransplantMMDD, transplantYear);
        if (transplantFullDate) {
          item.transplantDate = parseLocalDate(transplantFullDate);
          item.canShowTransplant = !!item.transplantDate;
        }
      }

      // Fallback: calculate from weeks indoors
      if (!item.transplantDate && effectiveWeeksIndoors) {
        const transplantDate = new Date(plantDate);
        transplantDate.setDate(transplantDate.getDate() + (effectiveWeeksIndoors * 7));
        item.transplantDate = transplantDate;
        item.canShowTransplant = true;
      }

      // Harvest = transplant + days to production (only if we have days_to_production)
      if (item.transplantDate && daysToProduction && hasFrostDates) {
        const harvestDate = new Date(item.transplantDate);
        harvestDate.setDate(harvestDate.getDate() + daysToProduction);

        // Apply fall growth factor if planting is on/after midpoint
        if (isFallPlanting(plantDate, frostDates)) {
          harvestDate.setDate(harvestDate.getDate() + FALL_GROWTH_FACTOR_DAYS);
          item.hasFallFactor = true;
        }

        item.harvestDate = harvestDate;
        item.canShowHarvest = true;
      }
    } else {
      // Direct sow - harvest = plant date + days to production
      if (daysToProduction && hasFrostDates) {
        const harvestDate = new Date(plantDate);
        harvestDate.setDate(harvestDate.getDate() + daysToProduction);

        // Apply fall growth factor if planting is on/after midpoint
        if (isFallPlanting(plantDate, frostDates)) {
          harvestDate.setDate(harvestDate.getDate() + FALL_GROWTH_FACTOR_DAYS);
          item.hasFallFactor = true;
        }

        item.harvestDate = harvestDate;
        item.canShowHarvest = true;
      }
    }

    schedule.push(item);
  }

  return {
    schedule,
    isIndoor,
    hasFrostDates,
    hasSeedData: !!seedData,
    hasSeason: !!season,
    season,
    hasDaysToProduction: !!daysToProduction,
    hasWeeksIndoors: !!weeksIndoors,
    usingSeasonDefault: isIndoor && !weeksIndoors && !!season
  };
};

/**
 * Calculate harvest estimate for a single planting (for display on cards/edit modal).
 * 
 * @param {Object} planting - Planting object from database
 * @param {Array} seeds - Array of seed objects for looking up production data
 * @param {Object} frostDates - Object with lastFrost and firstFrost date strings
 * @returns {Object|null} Harvest estimate with type, harvestDate, daysRemaining, etc.
 */
export const getHarvestEstimate = (planting, seeds, frostDates) => {
  if (!planting.plant) return null;

  const seedData = findSeedData(seeds, planting.plant, planting.variety);
  if (!seedData) return null;

  // Use `type` from seeds table; default to 'seed' for backwards compatibility
  const plantType = seedData.type || 'seed';
  const isAnnual = plantType === 'seed';

  // For perennials/trees/bushes, return harvest season and years info
  if (!isAnnual) {
    const seasons = normalizeHarvestSeason(seedData.harvest_season);
    // null = no season data; non-empty array = known season(s)
    const harvestSeason = seasons.length === 0 ? null : seasons;
    return {
      type: 'perennial',
      harvestSeason,
      yearsToProduction: seedData.years_to_production,
      category: seedData.category
    };
  }

  // For annuals, calculate harvest date
  const daysToProduction = seedData.days_to_production;
  const isIndoor = planting.start_method === 'indoor';
  const hasFrostDates = !!(frostDates?.lastFrost && frostDates?.firstFrost);

  // Don't show harvest estimate without days_to_production
  if (!daysToProduction) {
    return { type: 'annual', harvestDate: null, daysRemaining: null, missingDaysToProduction: true };
  }

  // Don't show harvest estimate without frost dates
  if (!hasFrostDates) {
    return { type: 'annual', harvestDate: null, daysRemaining: null, missingFrostDates: true };
  }

  const effectiveWeeksIndoors = getEffectiveWeeksIndoors(seedData);

  let harvestDate = null;
  let effectivePlantingDate = null;

  // Use started_date (actual) if available, otherwise fall back to start_date (planned)
  const effectiveStartDate = planting.started_date || planting.start_date;

  // If transplanted, use actual transplant date for harvest calc, but start date for fall factor
  if (planting.transplant_date) {
    const transplantDate = parseLocalDate(planting.transplant_date);
    effectivePlantingDate = parseLocalDate(effectiveStartDate);
    if (transplantDate) {
      harvestDate = new Date(transplantDate);
      harvestDate.setDate(harvestDate.getDate() + daysToProduction);
    }
  }
  // Indoor start - need weeks indoors (from seed data or season-based default)
  else if (isIndoor && effectiveStartDate && effectiveWeeksIndoors) {
    const startDate = parseLocalDate(effectiveStartDate);
    effectivePlantingDate = startDate;
    if (startDate) {
      const transplantDate = new Date(startDate);
      transplantDate.setDate(transplantDate.getDate() + (effectiveWeeksIndoors * 7));

      harvestDate = new Date(transplantDate);
      harvestDate.setDate(harvestDate.getDate() + daysToProduction);
    }
  }
  // Direct sow - harvest from plant date
  else if (!isIndoor && effectiveStartDate) {
    const startDate = parseLocalDate(effectiveStartDate);
    effectivePlantingDate = startDate;
    if (startDate) {
      harvestDate = new Date(startDate);
      harvestDate.setDate(harvestDate.getDate() + daysToProduction);
    }
  }

  // Apply fall growth factor if planting is on/after midpoint
  const hasFallFactor = isFallPlanting(effectivePlantingDate, frostDates);
  if (harvestDate && hasFallFactor) {
    harvestDate.setDate(harvestDate.getDate() + FALL_GROWTH_FACTOR_DAYS);
  }

  // Calculate days remaining
  let daysRemaining = null;
  if (harvestDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = harvestDate.getTime() - today.getTime();
    daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  return {
    type: 'annual',
    harvestDate,
    daysRemaining,
    daysToProduction: hasFallFactor ? daysToProduction + FALL_GROWTH_FACTOR_DAYS : daysToProduction
  };
};

// ============================================================================
// LAYOUT PLANT EXTRACTION
// ============================================================================

/**
 * Extract unique plants from garden layouts.
 * @param {Array} layouts - Array of layout objects with plants property
 * @returns {Array} Array of { plant, variety } objects, sorted alphabetically
 */
export const extractLayoutPlants = (layouts) => {
  const plantMap = new Map();

  layouts?.forEach(layout => {
    if (layout.plants) {
      Object.values(layout.plants).forEach(({ plant, variety }) => {
        if (plant) {
          const key = `${plant.toLowerCase()}|${(variety || '').toLowerCase()}`;
          if (!plantMap.has(key)) {
            plantMap.set(key, { plant, variety: variety || '' });
          }
        }
      });
    }
  });

  return Array.from(plantMap.values()).sort((a, b) =>
    a.plant.localeCompare(b.plant) || a.variety.localeCompare(b.variety)
  );
};

/**
 * Create a Set of layout plant keys for fast duplicate checking.
 * @param {Array} layoutPlants - Array from extractLayoutPlants
 * @returns {Set} Set of "plant|variety" lowercase keys
 */
export const createLayoutPlantKeySet = (layoutPlants) => {
  return new Set(layoutPlants.map(p => `${p.plant.toLowerCase()}|${(p.variety || '').toLowerCase()}`));
};

/**
 * Filter seeds to exclude those already in layout plants.
 * @param {Array} seeds - Array of seed objects
 * @param {Set} layoutPlantKeys - Set from createLayoutPlantKeySet
 * @returns {Array} Filtered seeds not already in layouts
 */
export const filterSeedsNotInLayout = (seeds, layoutPlantKeys) => {
  return seeds.filter(seed => {
    const key = `${seed.plant.toLowerCase()}|${(seed.variety || '').toLowerCase()}`;
    return !layoutPlantKeys.has(key);
  });
};

// ============================================================================
// ANNUAL SCHEDULE CALCULATION (for layout-based date generation)
// ============================================================================

/**
 * Threshold for long-season crops (days to production).
 * Crops exceeding this are flagged with a warning in fall mode.
 */
const LONG_SEASON_THRESHOLD_DAYS = 180;

/**
 * Calculate planting schedule for a single annual plant based on frost dates and season.
 * Used by both PDF export and Generate Plantings features.
 * 
 * @param {Object} options
 * @param {Object} options.seedData - Seed data object with days_to_production, weeks_indoors_before_transplant, season, preferred_start_method
 * @param {string} options.bedType - Bed type ('bed' or 'greenhouse')
 * @param {Date|null} options.lastFrostDate - Last frost date (parsed)
 * @param {Date|null} options.firstFrostDate - First frost date (parsed)
 * @param {boolean} options.isFallMode - Whether calculating for fall planting
 * @returns {Object} Schedule data with plantDate, method, transplantDate, harvestDate, warnings
 */
export const calculateAnnualSchedule = ({ seedData, bedType, lastFrostDate, firstFrostDate, isFallMode }) => {
  const daysToProduction = seedData?.days_to_production;
  const weeksIndoors = seedData?.weeks_indoors_before_transplant;
  const season = seedData?.season; // 'warm', 'cool', or null
  const preferredStartMethod = seedData?.preferred_start_method; // 'indoor', 'direct_sow', or null

  let plantDate = null;
  let method;
  let transplantDate = null;
  let harvestDate = null;
  let warnings = [];

  // Infer start method from available data
  let inferredMethod;
  let effectiveWeeksIndoors = null;

  if (preferredStartMethod === 'indoor') {
    inferredMethod = 'Start Indoors';
    effectiveWeeksIndoors = weeksIndoors || 8;
  } else if (preferredStartMethod === 'direct_sow') {
    inferredMethod = 'Direct Sow';
  } else if (weeksIndoors) {
    inferredMethod = 'Start Indoors';
    effectiveWeeksIndoors = weeksIndoors;
  } else if (season === 'warm') {
    inferredMethod = 'Start Indoors';
    effectiveWeeksIndoors = 8;
  } else {
    inferredMethod = 'Direct Sow';
  }

  if (isFallMode && firstFrostDate) {
    // === FALL MODE CALCULATIONS ===
    if (daysToProduction && daysToProduction > LONG_SEASON_THRESHOLD_DAYS) {
      warnings.push('NOTE: Long-season crop - may require fall planting for next year\'s harvest');
    }

    if (season === 'warm' && bedType !== 'greenhouse') {
      warnings.push('NOTE: Warm-season crop - consider adding row cover or cold frame');
    }

    if (inferredMethod === 'Start Indoors') {
      if (daysToProduction) {
        transplantDate = new Date(firstFrostDate);
        transplantDate.setDate(transplantDate.getDate() - (daysToProduction + 14));

        plantDate = new Date(transplantDate);
        plantDate.setDate(plantDate.getDate() - (effectiveWeeksIndoors * 7));

        harvestDate = new Date(transplantDate);
        harvestDate.setDate(harvestDate.getDate() + daysToProduction);
      } else {
        transplantDate = new Date(firstFrostDate);
        transplantDate.setDate(transplantDate.getDate() - 74);

        plantDate = new Date(transplantDate);
        plantDate.setDate(plantDate.getDate() - (effectiveWeeksIndoors * 7));
      }
      method = 'Start Indoors';
    } else {
      if (daysToProduction) {
        plantDate = new Date(firstFrostDate);
        plantDate.setDate(plantDate.getDate() - (daysToProduction + 14));

        harvestDate = new Date(plantDate);
        harvestDate.setDate(harvestDate.getDate() + daysToProduction);
      } else {
        plantDate = new Date(firstFrostDate);
        plantDate.setDate(plantDate.getDate() - 74);
      }
      method = 'Direct Sow';
      transplantDate = null;
    }
  } else if (!isFallMode && lastFrostDate) {
    // === SPRING/SUMMER MODE CALCULATIONS ===
    if (inferredMethod === 'Start Indoors') {
      transplantDate = new Date(lastFrostDate);
      if (season === 'cool') {
        transplantDate.setDate(transplantDate.getDate() - 14);
      } else if (season === 'warm') {
        transplantDate.setDate(transplantDate.getDate() + 7);
      }

      plantDate = new Date(transplantDate);
      plantDate.setDate(plantDate.getDate() - (effectiveWeeksIndoors * 7));
      method = 'Start Indoors';

      if (daysToProduction) {
        harvestDate = new Date(transplantDate);
        harvestDate.setDate(harvestDate.getDate() + daysToProduction);
      }
    } else {
      plantDate = new Date(lastFrostDate);
      if (season === 'cool') {
        plantDate.setDate(plantDate.getDate() - 28);
      } else {
        plantDate.setDate(plantDate.getDate() + 7);
      }
      method = 'Direct Sow';
      transplantDate = null;

      if (daysToProduction) {
        harvestDate = new Date(plantDate);
        harvestDate.setDate(harvestDate.getDate() + daysToProduction);
      }
    }
  } else {
    plantDate = null;
    method = inferredMethod;
  }

  return { plantDate, method, transplantDate, harvestDate, warnings };
};

/**
 * Extract unique plant assignments from a layout with matched seed data.
 * Plants are deduplicated across all beds by plant+variety key.
 * 
 * @param {Object} layout - Layout object with elements and plants
 * @param {Array} seeds - Array of seed objects from database
 * @returns {Object} { annuals: Array, perennials: Array, customPlants: Array }
 *   - annuals: Plants with seed data that are type 'seed'
 *   - perennials: Plants with seed data that are non-annual type
 *   - customPlants: Plants in layout with no matching seed entry
 */
export const extractLayoutPlantAssignments = (layout, seeds) => {
  if (!layout?.elements || !layout?.plants) return { annuals: [], perennials: [], customPlants: [] };

  const beds = layout.elements.filter(el => el.type === 'bed' || el.type === 'greenhouse');
  const plantMap = new Map();

  beds.forEach(bed => {
    (bed.cells || []).forEach(cell => {
      const rawVal = layout.plants[`${bed.id}-${cell}`];
      const plants = normalizeCellPlants(rawVal);
      plants.forEach(plant => {
        if (!plant?.plant) return;
        const key = `${(plant.plant || '').toLowerCase()}|${(plant.variety || '').toLowerCase()}`;
        if (!plantMap.has(key)) {
          plantMap.set(key, {
            plant: plant.plant,
            variety: plant.variety || '',
            bedType: bed.type,
            totalQuantity: plant.quantity || 1,
            bedNames: new Set([bed.name || 'Bed']),
            seedData: null
          });
        } else {
          // Aggregate quantity across cells for the same plant
          const entry = plantMap.get(key);
          entry.totalQuantity += (plant.quantity || 1);
          entry.bedNames.add(bed.name || 'Bed');
        }
      });
    });
  });

  // Match each unique plant to seed data
  const allPlants = Array.from(plantMap.values()).sort((a, b) =>
    a.plant.localeCompare(b.plant) || a.variety.localeCompare(b.variety)
  );

  // Convert bed name Sets to sorted arrays
  allPlants.forEach(p => {
    p.bedNames = Array.from(p.bedNames).sort();
  });

  allPlants.forEach(p => {
    p.seedData = findSeedData(seeds, p.plant, p.variety);
  });

  const annuals = allPlants.filter(p => p.seedData && (p.seedData.type === 'seed' || !p.seedData.type));
  const perennials = allPlants.filter(p => p.seedData && (p.seedData.type === 'perennial' || p.seedData.type === 'tree' || p.seedData.type === 'bush'));
  const customPlants = allPlants.filter(p => !p.seedData);

  return { annuals, perennials, customPlants };
};
